import React, { useState } from 'react';
import { Card } from './ui/Card';
import { ManagerTeamGoal } from '../types/goals';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { ArrowUpDown, Plus, Clock, Zap, Eye, Target } from 'lucide-react';
import { GoalSettingModal } from './GoalSettingModal';
import { FileListModal } from './FileListModal';
import { LoanFile } from '../types/goals';
interface ManagerGoalTableProps {
  teamGoal: ManagerTeamGoal | null;
}
// Mock file data generator for each processor
const generateProcessorFiles = (processorName: string, stage: 'awc' | 'appraisalBack', count: number): LoanFile[] => {
  const awcStatuses = ['Waiting on income verification', 'Pending title review', 'Appraisal ordered', 'Waiting on HOA documents', 'Insurance verification needed', 'Credit supplement requested'];
  const appraisalStatuses = ['Appraisal review in progress', 'Value concerns - reviewing comps', 'Appraisal cleared', 'Waiting on final conditions'];
  const borrowerNames = ['John & Sarah Martinez', 'Robert Thompson', 'Emily & David Wilson', 'Lisa Anderson', 'Michael & Jennifer Brown', 'Christopher Lee', 'Patricia & James Garcia', 'Daniel Rodriguez', 'Thomas & Maria Johnson', 'Susan White'];
  const addresses = ['123 Oak Street, San Diego, CA', '456 Maple Ave, Austin, TX', '789 Pine Road, Seattle, WA', '321 Elm Street, Portland, OR', '654 Cedar Lane, Denver, CO', '987 Birch Court, Phoenix, AZ', '147 Willow Drive, Nashville, TN', '258 Spruce Street, Charlotte, NC', '369 Redwood Ave, Sacramento, CA', '741 Cypress Road, Tampa, FL'];
  const statuses = stage === 'awc' ? awcStatuses : appraisalStatuses;
  return Array.from({
    length: count
  }, (_, i) => ({
    id: `${processorName}-${stage}-${i}`,
    borrowerName: borrowerNames[i % borrowerNames.length],
    loanAmount: Math.floor(Math.random() * 300000) + 250000,
    propertyAddress: addresses[i % addresses.length],
    daysInStage: Math.floor(Math.random() * 7) + 1,
    loanOfficer: processorName,
    status: statuses[i % statuses.length],
    priority: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)] as 'high' | 'medium' | 'low',
    openConditions: stage === 'awc' ? Math.floor(Math.random() * 6) + 2 : Math.floor(Math.random() * 3) + 1
  }));
};
export function ManagerGoalTable({
  teamGoal
}: ManagerGoalTableProps) {
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [fileModalState, setFileModalState] = useState<{
    isOpen: boolean;
    stage: 'awc' | 'appraisalBack' | null;
    processorName: string;
    files: LoanFile[];
  }>({
    isOpen: false,
    stage: null,
    processorName: '',
    files: []
  });
  const handleSaveGoals = (goals: any[]) => {
    console.log('Saving goals:', goals);
    alert(`Successfully added goals for ${goals.length} processor(s)!`);
  };
  const handleBadgeClick = (e: React.MouseEvent, processorName: string, stage: 'awc' | 'appraisalBack', count: number) => {
    e.stopPropagation();
    if (count === 0) return;
    const files = generateProcessorFiles(processorName, stage, count);
    setFileModalState({
      isOpen: true,
      stage,
      processorName,
      files
    });
  };
  const closeFileModal = () => {
    setFileModalState({
      isOpen: false,
      stage: null,
      processorName: '',
      files: []
    });
  };
  if (!teamGoal || !teamGoal.processors || teamGoal.processors.length === 0) {
    return <>
        <Card className="text-center py-12" data-id="element-102">
          <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-103">
            No Team Data
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-104">
            Start by setting up your processing team and their goals.
          </p>
          <Button onClick={() => setIsGoalModalOpen(true)} data-id="element-105">
            <Plus className="w-4 h-4 mr-2" data-id="element-106" />
            Set Team Goals
          </Button>
        </Card>

        <GoalSettingModal isOpen={isGoalModalOpen} onClose={() => setIsGoalModalOpen(false)} onSave={handleSaveGoals} data-id="element-107" />
      </>;
  }
  // Calculate team totals
  const teamTotals = teamGoal.processors.reduce((acc, processor) => ({
    underwriting: acc.underwriting + processor.pipeline.underwriting,
    awc: acc.awc + processor.pipeline.awc,
    appraisalBack: acc.appraisalBack + processor.pipeline.appraisalBack,
    ctcGoal: acc.ctcGoal + processor.ctcGoal,
    ctcActual: acc.ctcActual + processor.ctcActual
  }), {
    underwriting: 0,
    awc: 0,
    appraisalBack: 0,
    ctcGoal: 0,
    ctcActual: 0
  });
  return <>
      <div className="space-y-6" data-id="element-108">
        {/* Team Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-109">
          <div className="bg-white rounded-lg border border-slate-200 p-4" data-id="element-110">
            <div className="flex items-center gap-3 mb-2" data-id="element-111">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center" data-id="element-112">
                <Clock className="w-5 h-5 text-slate-500" data-id="element-113" />
              </div>
              <div data-id="element-114">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-medium" data-id="element-115">
                  Underwriting
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-116">
                  {teamTotals.underwriting}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500" data-id="element-117">Coming up next</p>
          </div>

          <div className="bg-white rounded-lg border-2 border-blue-200 p-4" data-id="element-118">
            <div className="flex items-center gap-3 mb-2" data-id="element-119">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-120">
                <Zap className="w-5 h-5 text-blue-600" data-id="element-121" />
              </div>
              <div data-id="element-122">
                <div className="text-xs text-blue-600 uppercase tracking-wider font-medium" data-id="element-123">
                  AWC - Active
                </div>
                <div className="text-2xl font-bold text-blue-900" data-id="element-124">
                  {teamTotals.awc}
                </div>
              </div>
            </div>
            <p className="text-xs text-blue-700" data-id="element-125">Needs attention</p>
          </div>

          <div className="bg-white rounded-lg border border-amber-200 p-4" data-id="element-126">
            <div className="flex items-center gap-3 mb-2" data-id="element-127">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-128">
                <Eye className="w-5 h-5 text-amber-600" data-id="element-129" />
              </div>
              <div data-id="element-130">
                <div className="text-xs text-amber-600 uppercase tracking-wider font-medium" data-id="element-131">
                  Appraisal Back
                </div>
                <div className="text-2xl font-bold text-amber-900" data-id="element-132">
                  {teamTotals.appraisalBack}
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-700" data-id="element-133">Monitor closely</p>
          </div>

          <div className="bg-white rounded-lg border border-emerald-200 p-4" data-id="element-134">
            <div className="flex items-center gap-3 mb-2" data-id="element-135">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-136">
                <Target className="w-5 h-5 text-emerald-600" data-id="element-137" />
              </div>
              <div data-id="element-138">
                <div className="text-xs text-emerald-600 uppercase tracking-wider font-medium" data-id="element-139">
                  CTC
                </div>
                <div className="text-2xl font-bold text-emerald-900" data-id="element-140">
                  {teamTotals.ctcActual}
                  <span className="text-lg text-slate-400 font-medium" data-id="element-141">
                    {' '}
                    / {teamTotals.ctcGoal}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-emerald-700" data-id="element-142">
              {Math.round(teamTotals.ctcActual / teamTotals.ctcGoal * 100)}%
              of goal
            </p>
          </div>
        </div>

        {/* Processor Pipeline Table */}
        <Card title="Processor Pipeline Overview" description="Track pipeline stages across your processing team. Click file counts to view and add notes." action={<Button size="sm" onClick={() => setIsGoalModalOpen(true)} data-id="element-144">
              <Plus className="w-4 h-4 mr-2" data-id="element-145" /> Add Processor
            </Button>} className="overflow-hidden" data-id="element-143">
          <div className="overflow-x-auto -mx-6" data-id="element-146">
            <table className="w-full text-left text-sm" data-id="element-147">
              <thead className="bg-slate-50 border-b border-slate-200" data-id="element-148">
                <tr data-id="element-149">
                  <th className="px-6 py-3 font-semibold text-slate-900" data-id="element-150">
                    Processor
                  </th>
                  <th className="px-6 py-3 font-semibold text-slate-700 text-center border-l border-slate-200" data-id="element-151">
                    <div className="flex items-center justify-center gap-2" data-id="element-152">
                      <Clock className="w-4 h-4" data-id="element-153" />
                      Underwriting
                    </div>
                  </th>
                  <th className="px-6 py-3 font-semibold text-blue-700 text-center border-l border-slate-200 bg-blue-50/30" data-id="element-154">
                    <div className="flex items-center justify-center gap-2" data-id="element-155">
                      <Zap className="w-4 h-4" data-id="element-156" />
                      AWC
                    </div>
                  </th>
                  <th className="px-6 py-3 font-semibold text-amber-700 text-center border-l border-slate-200" data-id="element-157">
                    <div className="flex items-center justify-center gap-2" data-id="element-158">
                      <Eye className="w-4 h-4" data-id="element-159" />
                      Appraisal Back
                    </div>
                  </th>
                  <th className="px-6 py-3 font-semibold text-emerald-700 text-center border-l border-slate-200" data-id="element-160">
                    <div className="flex items-center justify-center gap-2" data-id="element-161">
                      <Target className="w-4 h-4" data-id="element-162" />
                      CTC Goal
                    </div>
                  </th>
                  <th className="px-6 py-3 font-semibold text-emerald-700 text-center" data-id="element-163">
                    CTC Actual
                  </th>
                  <th className="px-6 py-3 font-semibold text-slate-700 text-center" data-id="element-164">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-165">
                {teamGoal.processors.map((processor, idx) => {
                const ctcProgress = Math.round(processor.ctcActual / processor.ctcGoal * 100);
                return <tr key={processor.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} data-id="element-166">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-167">
                        {processor.name}
                      </td>

                      {/* Underwriting */}
                      <td className="px-6 py-4 text-center border-l border-slate-100" data-id="element-168">
                        <Badge variant="neutral" data-id="element-169">
                          {processor.pipeline.underwriting} files
                        </Badge>
                      </td>

                      {/* AWC - Clickable */}
                      <td className="px-6 py-4 text-center border-l border-slate-100 bg-blue-50/20" data-id="element-170">
                        <Badge variant="info" onClick={processor.pipeline.awc > 0 ? e => handleBadgeClick(e, processor.name, 'awc', processor.pipeline.awc) : undefined} data-id="element-171">
                          {processor.pipeline.awc} files
                        </Badge>
                      </td>

                      {/* Appraisal Back - Clickable */}
                      <td className="px-6 py-4 text-center border-l border-slate-100" data-id="element-172">
                        <Badge variant="warning" onClick={processor.pipeline.appraisalBack > 0 ? e => handleBadgeClick(e, processor.name, 'appraisalBack', processor.pipeline.appraisalBack) : undefined} data-id="element-173">
                          {processor.pipeline.appraisalBack} files
                        </Badge>
                      </td>

                      {/* CTC Goal */}
                      <td className="px-6 py-4 text-center text-slate-500 border-l border-slate-100" data-id="element-174">
                        {processor.ctcGoal}
                      </td>

                      {/* CTC Actual */}
                      <td className="px-6 py-4 text-center font-semibold text-slate-900" data-id="element-175">
                        {processor.ctcActual}
                      </td>

                      {/* Progress */}
                      <td className="px-6 py-4 text-center" data-id="element-176">
                        <Badge variant={ctcProgress >= 100 ? 'success' : ctcProgress >= 80 ? 'warning' : 'danger'} data-id="element-177">
                          {ctcProgress}%
                        </Badge>
                      </td>
                    </tr>;
              })}

                {/* Team Totals Row */}
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-200" data-id="element-178">
                  <td className="px-6 py-4 text-slate-900" data-id="element-179">Team Totals</td>

                  <td className="px-6 py-4 text-center border-l border-slate-200" data-id="element-180">
                    <Badge variant="neutral" data-id="element-181">
                      {teamTotals.underwriting} files
                    </Badge>
                  </td>

                  <td className="px-6 py-4 text-center border-l border-slate-200 bg-blue-50/30" data-id="element-182">
                    <Badge variant="info" data-id="element-183">{teamTotals.awc} files</Badge>
                  </td>

                  <td className="px-6 py-4 text-center border-l border-slate-200" data-id="element-184">
                    <Badge variant="warning" data-id="element-185">
                      {teamTotals.appraisalBack} files
                    </Badge>
                  </td>

                  <td className="px-6 py-4 text-center text-slate-700 border-l border-slate-200" data-id="element-186">
                    {teamTotals.ctcGoal}
                  </td>

                  <td className="px-6 py-4 text-center text-slate-900" data-id="element-187">
                    {teamTotals.ctcActual}
                  </td>

                  <td className="px-6 py-4 text-center" data-id="element-188">
                    <Badge variant={teamTotals.ctcActual >= teamTotals.ctcGoal ? 'success' : teamTotals.ctcActual >= teamTotals.ctcGoal * 0.8 ? 'warning' : 'danger'} data-id="element-189">
                      {Math.round(teamTotals.ctcActual / teamTotals.ctcGoal * 100)}
                      %
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <GoalSettingModal isOpen={isGoalModalOpen} onClose={() => setIsGoalModalOpen(false)} onSave={handleSaveGoals} data-id="element-190" />

      {fileModalState.stage && <FileListModal isOpen={fileModalState.isOpen} onClose={closeFileModal} stage={fileModalState.stage} files={fileModalState.files} processorName={fileModalState.processorName} data-id="element-191" />}
    </>;
}